// src/pages/admin/TicketsPage.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminTicketsApi } from '@/api/services'
import toast from 'react-hot-toast'
import { MessageSquare, Send, ChevronLeft, User } from 'lucide-react'

const STATUS_COLORS = {
  open:           'bg-blue-100 text-blue-700',
  in_progress:    'bg-amber-100 text-amber-700',
  awaiting_reply: 'bg-purple-100 text-purple-700',
  resolved:       'bg-green-100 text-green-700',
  closed:         'bg-gray-100 text-gray-600',
}

const PRIORITY_DOT = {
  low:    'bg-gray-400',
  medium: 'bg-blue-400',
  high:   'bg-amber-400',
  urgent: 'bg-red-500',
}

export default function TicketsPage() {
  const qc = useQueryClient()
  const [selectedId, setSelectedId] = useState(null)
  const [statusFilter, setStatusFilter] = useState('')
  const [replyText, setReplyText] = useState('')
  const [isInternal, setIsInternal] = useState(false)

  const { data: listData, isLoading } = useQuery({
    queryKey: ['admin-tickets', statusFilter],
    queryFn: () => adminTicketsApi.list({ status: statusFilter || undefined }).then(r => r.data),
  })

  const { data: detailData } = useQuery({
    queryKey: ['admin-ticket', selectedId],
    queryFn: () => adminTicketsApi.show(selectedId).then(r => r.data),
    enabled: !!selectedId,
  })

  const replyMut = useMutation({
    mutationFn: ({ id, body, is_internal }) => adminTicketsApi.reply(id, { body, is_internal }),
    onSuccess: () => {
      setReplyText('')
      qc.invalidateQueries({ queryKey: ['admin-ticket', selectedId] })
      qc.invalidateQueries({ queryKey: ['admin-tickets'] })
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Reply failed'),
  })

  const statusMut = useMutation({
    mutationFn: ({ id, status }) => adminTicketsApi.updateStatus(id, { status }),
    onSuccess: () => {
      toast.success('Status updated')
      qc.invalidateQueries({ queryKey: ['admin-ticket', selectedId] })
      qc.invalidateQueries({ queryKey: ['admin-tickets'] })
    },
  })

  const tickets = listData?.data?.data ?? listData?.data ?? []
  const ticket = detailData?.data ?? null

  if (selectedId && ticket) {
    return (
      <div className="space-y-4">
        <button onClick={() => setSelectedId(null)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <div className="bg-white rounded-lg border p-5">
          <div className="flex items-start justify-between mb-1">
            <div>
              <h2 className="text-lg font-bold text-gray-900">{ticket.subject}</h2>
              <div className="text-xs text-gray-500 mt-1">
                {ticket.ticket_number} &middot; {ticket.category?.replace(/_/g, ' ')} &middot;
                <User className="inline w-3 h-3 ml-1" /> {ticket.user?.name} ({ticket.user?.email})
                {ticket.order && <> &middot; Order #{ticket.order.order_number}</>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={ticket.status}
                onChange={e => statusMut.mutate({ id: ticket.id, status: e.target.value })}
                className="border rounded px-2 py-1 text-xs"
              >
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="awaiting_reply">Awaiting Reply</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>

          <div className="space-y-3 my-4 max-h-[28rem] overflow-y-auto">
            {(ticket.messages || []).map(msg => (
              <div key={msg.id} className={`p-3 rounded-lg text-sm ${msg.is_internal ? 'bg-yellow-50 border border-yellow-200' : msg.user?.role === 'admin' ? 'bg-blue-50 ml-4' : 'bg-gray-50 mr-4'}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-gray-800">
                    {msg.user?.name} {msg.is_internal && <span className="text-yellow-600 text-xs">(internal note)</span>}
                  </span>
                  <span className="text-xs text-gray-400">{new Date(msg.created_at).toLocaleString('en-IN')}</span>
                </div>
                <p className="text-gray-700 whitespace-pre-wrap">{msg.body}</p>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <div className="flex gap-2">
              <input
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                placeholder={isInternal ? 'Internal note (not visible to user)...' : 'Type your reply...'}
                className={`flex-1 border rounded-lg px-3 py-2 text-sm ${isInternal ? 'border-yellow-300 bg-yellow-50' : ''}`}
              />
              <button
                onClick={() => replyMut.mutate({ id: ticket.id, body: replyText, is_internal: isInternal })}
                disabled={!replyText.trim() || replyMut.isPending}
                className="bg-pink-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-pink-600 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <label className="flex items-center gap-2 text-xs text-gray-500">
              <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} className="rounded border-gray-300" />
              Internal note (not visible to customer)
            </label>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Support Tickets</h1>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="border rounded px-3 py-1.5 text-sm"
        >
          <option value="">All</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="awaiting_reply">Awaiting Reply</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No tickets found</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-gray-600">
                <th className="px-4 py-3 font-medium">Ticket</th>
                <th className="px-4 py-3 font-medium">User</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">Priority</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Msgs</th>
                <th className="px-4 py-3 font-medium">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {tickets.map(t => (
                <tr key={t.id} onClick={() => setSelectedId(t.id)} className="hover:bg-gray-50 cursor-pointer">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{t.subject}</div>
                    <div className="text-xs text-gray-500">{t.ticket_number}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">{t.user?.name}<br /><span className="text-gray-400">{t.user?.email}</span></td>
                  <td className="px-4 py-3 text-xs capitalize">{t.category?.replace(/_/g, ' ')}</td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1 text-xs capitalize">
                      <span className={`w-2 h-2 rounded-full ${PRIORITY_DOT[t.priority]}`} />
                      {t.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[t.status]}`}>
                      {t.status?.replace(/_/g, ' ').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center text-xs">{t.messages_count || 0}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{new Date(t.updated_at).toLocaleDateString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
